import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { formatDateTimeLocale } from '../../utils/dateFormat';
import { useNavigate } from 'react-router-dom';
import { useTreatXAdminAuth } from '../../context/TreatXAdminAuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Switch } from '../../components/ui/switch';
import { Label } from '../../components/ui/label';
import { Input } from '../../components/ui/input';
import { toast } from 'sonner';
import {
  ArrowLeft, Shield, Activity, Clock, Server,
  ChevronDown, ChevronUp, Save, Loader2, Search,
  AlertTriangle, Zap, RefreshCw, Database
} from 'lucide-react';
import api from '../../utils/api';


function getAuthHeaders() {
  return { 'Content-Type': 'application/json' };
}

const BOUNDS = {
  confirm_after_hours: { min: 0, max: 8760 },
  release_after_hours: { min: 0, max: 8760 },
  max_auto_releases_per_day: { min: 1, max: 1000 },
};

/* ─── Health Panel ─────────────────────────────────────────── */
function HealthPanel({ health, loading, onRefresh }) {
  if (loading) return <div className="h-28 bg-slate-50 animate-pulse rounded-lg" data-testid="health-loading" />;
  if (!health) return null;
  return (
    <Card className="border border-slate-200/60 shadow-sm" data-testid="health-panel">
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <CardTitle className="text-sm font-semibold text-[#122033] font-[Manrope] flex items-center gap-2">
            <Activity className="h-4 w-4 text-emerald-500" /> Scheduler Health
          </CardTitle>
          <Button variant="ghost" size="sm" onClick={onRefresh} className="h-7 w-7 p-0" data-testid="refresh-health">
            <RefreshCw className="h-3.5 w-3.5 text-slate-400" />
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-2">
          <Indicator label="Enabled" value={health.enabled} type="bool" testId="health-enabled" />
          <Indicator label="Dry Run" value={health.dry_run} type="bool" warn testId="health-dry-run" />
          <Indicator label="Interval" value={`${health.interval_seconds}s`} testId="health-interval" />
          <Indicator label="Max Batch" value={health.max_batch} testId="health-batch" />
          <Indicator label="Last Tick" value={health.last_tick_utc ? new Date(health.last_tick_utc).toLocaleTimeString() : 'Never'} testId="health-last-tick" />
          <Indicator label="Lock Mode" value={health.lock_mode_used || 'none'} testId="health-lock" />
        </div>
        {!health.enabled && (
          <div className="mt-3 bg-amber-50 border border-amber-100 rounded-md px-3 py-2 flex items-center gap-2" data-testid="scheduler-off-warning">
            <AlertTriangle className="h-3.5 w-3.5 text-amber-500" />
            <p className="text-xs text-amber-700">Scheduler is currently disabled. Auto-release will not process any milestones.</p>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function Indicator({ label, value, type, warn, testId }) {
  let display = String(value);
  let color = 'text-slate-700';
  if (type === 'bool') {
    display = value ? 'ON' : 'OFF';
    color = value ? (warn ? 'text-amber-600' : 'text-emerald-600') : 'text-slate-400';
  }
  return (
    <div className="bg-slate-50 rounded-md px-2.5 py-1.5" data-testid={testId}>
      <p className="text-[9px] text-slate-400 uppercase tracking-wider">{label}</p>
      <p className={`text-sm font-bold font-[Manrope] ${color}`}>{display}</p>
    </div>
  );
}

/* ─── Stats Panel ──────────────────────────────────────────── */
function StatsPanel({ stats, loading, onRefresh }) {
  const [showTicks, setShowTicks] = useState(false);
  if (loading) return <div className="h-28 bg-slate-50 animate-pulse rounded-lg" data-testid="stats-loading" />;
  if (!stats) return null;
  const t = stats.totals || {};
  const ticks = stats.recent_ticks || [];
  const clinicCounts = stats.per_clinic_counts || [];
  return (
    <Card className="border border-slate-200/60 shadow-sm" data-testid="stats-panel">
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <CardTitle className="text-sm font-semibold text-[#122033] font-[Manrope] flex items-center gap-2">
            <Database className="h-4 w-4 text-blue-500" /> Scheduler Stats
          </CardTitle>
          <Button variant="ghost" size="sm" onClick={onRefresh} className="h-7 w-7 p-0" data-testid="refresh-stats">
            <RefreshCw className="h-3.5 w-3.5 text-slate-400" />
          </Button>
        </div>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-2" data-testid="stats-totals">
          <StatBox label="Total Ticks" value={t.total_ticks} />
          <StatBox label="Confirmed" value={t.total_confirmed} color="text-emerald-700" />
          <StatBox label="Released" value={t.total_released} color="text-blue-700" />
          <StatBox label="Errors" value={t.total_errors} color={t.total_errors > 0 ? 'text-red-600' : 'text-slate-700'} />
        </div>

        {/* Recent Ticks */}
        {ticks.length > 0 && (
          <div>
            <button className="flex items-center gap-1 text-xs text-slate-400 hover:text-slate-600" onClick={() => setShowTicks(!showTicks)} data-testid="toggle-ticks">
              {showTicks ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />}
              Last {ticks.length} ticks
            </button>
            {showTicks && (
              <div className="mt-2 overflow-x-auto" data-testid="ticks-table">
                <table className="w-full text-xs">
                  <thead>
                    <tr className="border-b border-slate-100 text-[10px] text-slate-400 uppercase">
                      <th className="py-1 text-left">Time</th>
                      <th className="py-1 text-right">Confirmed</th>
                      <th className="py-1 text-right">Released</th>
                      <th className="py-1 text-right">Skipped</th>
                      <th className="py-1 text-right">Errors</th>
                    </tr>
                  </thead>
                  <tbody>
                    {ticks.map((tk, i) => (
                      <tr key={i} className="border-b border-slate-50">
                        <td className="py-1 text-slate-500">{formatDateTimeLocale(tk.tick_utc)}</td>
                        <td className="py-1 text-right text-emerald-600">{tk.confirmed}</td>
                        <td className="py-1 text-right text-blue-600">{tk.released}</td>
                        <td className="py-1 text-right text-slate-400">{tk.skipped}</td>
                        <td className="py-1 text-right text-red-500">{tk.errors}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {/* Per-Clinic Counts */}
        {clinicCounts.length > 0 && (
          <div data-testid="per-clinic-counts">
            <p className="text-[10px] text-slate-400 uppercase tracking-wider mb-1">Top Clinics by Activity</p>
            <div className="flex flex-wrap gap-1.5">
              {clinicCounts.slice(0, 20).map((c, i) => (
                <Badge key={i} variant="outline" className="text-[10px] px-1.5 py-0 border-slate-200">
                  {c.clinic_id}: {c.count}
                </Badge>
              ))}
            </div>
          </div>
        )}

        {ticks.length === 0 && clinicCounts.length === 0 && (
          <p className="text-xs text-slate-400 text-center py-2" data-testid="stats-empty">No scheduler activity recorded yet.</p>
        )}
      </CardContent>
    </Card>
  );
}

function StatBox({ label, value, color = 'text-[#122033]' }) {
  return (
    <div className="bg-slate-50 rounded-md px-2.5 py-1.5">
      <p className="text-[9px] text-slate-400 uppercase tracking-wider">{label}</p>
      <p className={`text-lg font-bold font-[Manrope] ${color}`}>{value ?? 0}</p>
    </div>
  );
}

/* ─── Clinic Override Panel ────────────────────────────────── */
function ClinicOverridePanel() {
  const [clinicId, setClinicId] = useState('');
  const [config, setConfig] = useState(null);
  const [source, setSource] = useState('');
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState(null);
  const [original, setOriginal] = useState(null);

  const loadConfig = async () => {
    if (!clinicId.trim()) { toast.error('Enter a clinic ID'); return; }
    setLoading(true);
    try {
      const res = await api.get(`/admin/auto-release/config/${clinicId.trim()}`);
      const data = res.data;
      const c = data.auto_release_config || {};
      setConfig(c);
      setSource(data.source || 'system_defaults');
      const fd = {
        enabled: c.enabled ?? true,
        auto_confirm_enabled: c.auto_confirm_enabled ?? true,
        auto_release_enabled: c.auto_release_enabled ?? true,
        confirm_after_hours: c.confirm_after_hours ?? 12,
        release_after_hours: c.release_after_hours ?? 0,
        require_escrow_fully_funded: c.require_escrow_fully_funded ?? true,
        max_auto_releases_per_day: c.max_auto_releases_per_day ?? 100,
      };
      setForm(fd);
      setOriginal(fd);
      toast.success(`Loaded config for ${clinicId.trim()}`);
    } catch (e) {
      toast.error(`Failed to load: ${e.message}`);
    } finally {
      setLoading(false);
    }
  };

  const saveConfig = async () => {
    if (!clinicId.trim() || !form) return;
    setSaving(true);
    try {
      const res = await api.post(`/admin/auto-release/config/${clinicId.trim()}`, form);
      if (res.status === 400) {
        const d = res.data;
        toast.error(d.detail || 'Validation error');
        return;
      }
      const data = res.data;
      const c = data.auto_release_config || {};
      setConfig(c);
      setSource(data.source || 'clinic_custom');
      const fd = {
        enabled: c.enabled ?? true,
        auto_confirm_enabled: c.auto_confirm_enabled ?? true,
        auto_release_enabled: c.auto_release_enabled ?? true,
        confirm_after_hours: c.confirm_after_hours ?? 12,
        release_after_hours: c.release_after_hours ?? 0,
        require_escrow_fully_funded: c.require_escrow_fully_funded ?? true,
        max_auto_releases_per_day: c.max_auto_releases_per_day ?? 100,
      };
      setForm(fd);
      setOriginal(fd);
      toast.success(`Config saved for ${clinicId.trim()}`);
    } catch (e) {
      toast.error(`Failed to save: ${e.message}`);
    } finally {
      setSaving(false);
    }
  };

  const hasChanges = useMemo(() => {
    if (!form || !original) return false;
    return Object.keys(form).some(k => form[k] !== original[k]);
  }, [form, original]);

  const setField = (field, value) => setForm(prev => ({ ...prev, [field]: value }));

  return (
    <Card className="border border-slate-200/60 shadow-sm" data-testid="clinic-override-panel">
      <CardHeader className="pb-2">
        <CardTitle className="text-sm font-semibold text-[#122033] font-[Manrope] flex items-center gap-2">
          <Server className="h-4 w-4 text-violet-500" /> Clinic Override
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Search */}
        <div className="flex gap-2" data-testid="clinic-search-row">
          <Input
            placeholder="clinic-001"
            value={clinicId}
            onChange={(e) => setClinicId(e.target.value)}
            className="max-w-[240px]"
            data-testid="clinic-id-input"
          />
          <Button onClick={loadConfig} disabled={loading} size="sm" className="gap-1" data-testid="load-config-btn">
            {loading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Search className="h-3.5 w-3.5" />}
            Load
          </Button>
        </div>

        {/* Config editor (after load) */}
        {form && (
          <div className="space-y-3 border-t border-slate-100 pt-3" data-testid="override-form">
            <div className="flex items-center gap-2 flex-wrap">
              <Badge variant="outline" className={`text-xs px-2 py-0.5 ${source === 'clinic_custom' ? 'border-emerald-200 text-emerald-700 bg-emerald-50' : 'border-slate-200 text-slate-500'}`} data-testid="override-source">
                {source === 'clinic_custom' ? 'Custom Config' : 'System Defaults'}
              </Badge>
              {config?.version != null && (
                <Badge variant="outline" className="text-[10px] px-1.5 py-0 border-slate-200 text-slate-400">v{config.version}</Badge>
              )}
            </div>

            {/* Warning */}
            <div className="bg-amber-50 border border-amber-100 rounded-md px-3 py-2 flex items-center gap-2" data-testid="override-warning">
              <AlertTriangle className="h-3.5 w-3.5 text-amber-500 flex-shrink-0" />
              <p className="text-[11px] text-amber-700">Changes apply immediately to this clinic's auto-release behavior.</p>
            </div>

            {/* Toggles grid */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <ToggleField label="Master Switch" checked={form.enabled} onChange={(v) => setField('enabled', v)} testId="override-enabled" />
              <ToggleField label="Auto-confirm" checked={form.auto_confirm_enabled} onChange={(v) => setField('auto_confirm_enabled', v)} testId="override-auto-confirm" />
              <ToggleField label="Auto-release" checked={form.auto_release_enabled} onChange={(v) => setField('auto_release_enabled', v)} testId="override-auto-release" />
              <ToggleField label="Require escrow funded" checked={form.require_escrow_fully_funded} onChange={(v) => setField('require_escrow_fully_funded', v)} testId="override-escrow" />
            </div>

            {/* Number inputs */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <div>
                <Label className="text-[10px] text-slate-400 uppercase tracking-wider">Confirm hours</Label>
                <Input type="number" min={0} max={8760} value={form.confirm_after_hours}
                  onChange={(e) => setField('confirm_after_hours', parseInt(e.target.value) || 0)}
                  data-testid="override-confirm-hours" />
              </div>
              <div>
                <Label className="text-[10px] text-slate-400 uppercase tracking-wider">Release hours</Label>
                <Input type="number" min={0} max={8760} value={form.release_after_hours}
                  onChange={(e) => setField('release_after_hours', parseInt(e.target.value) || 0)}
                  data-testid="override-release-hours" />
              </div>
              <div>
                <Label className="text-[10px] text-slate-400 uppercase tracking-wider">Daily cap</Label>
                <Input type="number" min={1} max={1000} value={form.max_auto_releases_per_day}
                  onChange={(e) => setField('max_auto_releases_per_day', parseInt(e.target.value) || 1)}
                  data-testid="override-daily-cap" />
              </div>
            </div>

            {/* Save */}
            <div className="flex justify-end">
              <Button onClick={saveConfig} disabled={!hasChanges || saving} className="bg-[#122033] hover:bg-[#1a2d45] text-white gap-2" data-testid="override-save-btn">
                {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
                {saving ? 'Saving...' : 'Save Override'}
              </Button>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function ToggleField({ label, checked, onChange, testId }) {
  return (
    <div className="flex items-center justify-between bg-slate-50 rounded-md px-3 py-2">
      <Label className="text-xs text-[#122033]">{label}</Label>
      <Switch checked={checked} onCheckedChange={onChange} data-testid={testId} />
    </div>
  );
}

/* ─── Main Admin Page ──────────────────────────────────────── */
/* MILESTONE-RECOVERY-MAKER-CHECKER-01 — two-person authorization
   for exceptional milestone overrides. Extends this page rather than
   creating a new milestone-management universe. Backend is
   authoritative for state and financial impact; this panel just
   sequences maker/checker + a mandatory reason. Reuses `finance.manage`
   plus a server-side TreatX-admin gate — no new permission introduced. */
function MilestoneRecoveryPanel({ currentActorId }) {
  const [tab, setTab] = React.useState('new');        // 'new' | 'queue'
  const [mid, setMid] = React.useState('');
  const [state, setState] = React.useState(null);
  const [busy, setBusy] = React.useState(false);
  const [err, setErr] = React.useState('');
  const [reason, setReason] = React.useState('');
  const [confirmAction, setConfirmAction] = React.useState(null);

  const [queue, setQueue] = React.useState([]);
  const [queueLoading, setQueueLoading] = React.useState(false);
  const [rejectFor, setRejectFor] = React.useState(null);
  const [rejectReason, setRejectReason] = React.useState('');

  const loadMilestone = async () => {
    if (!mid) return;
    setBusy(true); setErr(''); setState(null);
    try {
      const { data } = await api.get(`/milestones/${encodeURIComponent(mid)}`);
      setState(data?.milestone || data);
    } catch (e) {
      setErr(e?.response?.data?.message || e?.response?.data?.detail || e?.message || 'load failed');
    }
    setBusy(false);
  };

  const loadQueue = React.useCallback(async () => {
    setQueueLoading(true);
    try {
      const { data } = await api.get('/admin/milestones/recovery');
      setQueue(Array.isArray(data?.items) ? data.items : []);
    } catch (e) {
      setQueue([]);
    }
    setQueueLoading(false);
  }, []);

  React.useEffect(() => {
    if (tab === 'queue') loadQueue();
  }, [tab, loadQueue]);

  const submitRequest = async () => {
    if (!mid || !confirmAction) return;
    if ((reason || '').trim().length < 8) {
      setErr('Reason must be at least 8 characters (mandatory audit field).');
      return;
    }
    setBusy(true); setErr('');
    try {
      const key = `mrreq-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
      await api.post('/admin/milestones/recovery/request', {
        milestone_id: mid, action: confirmAction, reason: reason.trim(),
      }, { headers: { 'Idempotency-Key': key } });
      toast.success('Recovery request submitted', {
        description: 'Awaiting a second TreatX admin approval.',
      });
      setConfirmAction(null);
      setReason('');
      setState(null);
      setMid('');
      setTab('queue');
    } catch (e) {
      const msg = e?.response?.data?.detail || e?.response?.data?.message || e?.message || 'submit failed';
      setErr(String(msg));
    }
    setBusy(false);
  };

  const approveReq = async (reqId) => {
    setBusy(true);
    try {
      const key = `mrapp-${reqId}-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
      const { data } = await api.post(`/admin/milestones/recovery/${reqId}/approve`, {}, {
        headers: { 'Idempotency-Key': key },
      });
      if (data?.success) {
        toast.success('Override approved and executed');
      } else {
        toast.error('Approval recorded, execution failed', {
          description: data?.error || 'See queue for details',
        });
      }
      await loadQueue();
    } catch (e) {
      const msg = e?.response?.data?.detail || e?.response?.data?.message || e?.message || 'approve failed';
      toast.error(String(msg));
    }
    setBusy(false);
  };

  const rejectReq = async (reqId) => {
    setBusy(true);
    try {
      const key = `mrrej-${reqId}-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
      await api.post(`/admin/milestones/recovery/${reqId}/reject`,
        { reason: (rejectReason || '').trim() },
        { headers: { 'Idempotency-Key': key } });
      toast.success('Override rejected');
      setRejectFor(null);
      setRejectReason('');
      await loadQueue();
    } catch (e) {
      const msg = e?.response?.data?.detail || e?.response?.data?.message || e?.message || 'reject failed';
      toast.error(String(msg));
    }
    setBusy(false);
  };

  const status = state?.status;
  const canTrigger = status === 'pending' || status === 'frozen';
  const canRelease = status === 'achieved';

  return (
    <Card className="border border-rose-200/70 shadow-sm" data-testid="milestone-recovery-panel">
      <CardHeader className="pb-2">
        <CardTitle className="text-sm font-semibold text-rose-800 font-[Manrope] flex items-center gap-2">
          <Server className="h-4 w-4 text-rose-500" /> Recovery / Exceptional Override
        </CardTitle>
        <p className="text-xs text-slate-500">
          Not the normal provider workflow. Two-person authorization required:
          a second TreatX admin must approve every override before the milestone
          state changes. Backend enforces every transition and refuses self-approval.
        </p>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="flex items-center gap-2 border-b border-slate-100 pb-2">
          <button
            type="button"
            onClick={() => setTab('new')}
            className={`text-[12px] px-2 py-1 rounded ${tab === 'new'
              ? 'bg-rose-100 text-rose-800 font-semibold'
              : 'text-slate-500 hover:text-slate-800'}`}
            data-testid="recovery-tab-new"
          >New request</button>
          <button
            type="button"
            onClick={() => setTab('queue')}
            className={`text-[12px] px-2 py-1 rounded ${tab === 'queue'
              ? 'bg-rose-100 text-rose-800 font-semibold'
              : 'text-slate-500 hover:text-slate-800'}`}
            data-testid="recovery-tab-queue"
          >Pending approvals{queue.filter(q => q.status === 'pending').length > 0 && (
            <span className="ml-1 text-[10px] bg-rose-600 text-white rounded-full px-1.5 py-0.5">
              {queue.filter(q => q.status === 'pending').length}
            </span>
          )}</button>
        </div>

        {tab === 'new' && (
          <>
            <div className="flex gap-2">
              <Input placeholder="milestone_id" value={mid} onChange={(e) => setMid(e.target.value)}
                     className="max-w-[420px] font-mono" data-testid="recovery-mid-input" />
              <Button onClick={loadMilestone} disabled={busy || !mid} variant="outline"
                      data-testid="recovery-load-btn">Load</Button>
            </div>

            {err && (
              <div className="text-xs text-rose-700 border border-rose-200 bg-rose-50 rounded p-2"
                   data-testid="recovery-error">{err}</div>
            )}

            {state && (
              <div className="rounded border border-slate-200 bg-slate-50 p-3 text-sm"
                   data-testid="recovery-state-panel">
                <div className="grid grid-cols-2 gap-2">
                  <div><span className="text-[11px] uppercase text-slate-500 mr-1">Booking</span>
                       <span className="font-mono text-xs" data-testid="recovery-booking">{state.booking_id || '—'}</span></div>
                  <div><span className="text-[11px] uppercase text-slate-500 mr-1">Status</span>
                       <Badge className="ml-1" data-testid="recovery-status">{status || '—'}</Badge></div>
                  <div><span className="text-[11px] uppercase text-slate-500 mr-1">Amount</span>
                       <span data-testid="recovery-amount">
                         {state.amount_minor_units
                           ? `${(state.amount_minor_units/100).toFixed(2)} ${state.currency || ''}`
                           : state.amount_micros
                             ? `${(state.amount_micros/1_000_000).toFixed(2)} ${state.currency || ''}`
                             : '—'}
                       </span></div>
                  <div><span className="text-[11px] uppercase text-slate-500 mr-1">Sequence</span>
                       <span>{state.seq ?? state.sequence_number ?? '—'}</span></div>
                </div>
              </div>
            )}

            {state && (
              <div className="flex flex-wrap gap-2">
                <Button size="sm" variant="outline"
                  disabled={busy || !canTrigger}
                  onClick={() => { setConfirmAction('trigger'); setErr(''); }}
                  data-testid="recovery-trigger-btn">Request · Trigger override</Button>
                <Button size="sm"
                  className="bg-rose-600 hover:bg-rose-700 text-white"
                  disabled={busy || !canRelease}
                  onClick={() => { setConfirmAction('release'); setErr(''); }}
                  data-testid="recovery-release-btn">Request · Release override</Button>
              </div>
            )}

            {confirmAction && (
              <div className="border border-rose-300 bg-rose-50 rounded p-3 space-y-2"
                   data-testid="recovery-confirm-panel">
                <div className="text-sm">
                  Submit override request: <b>{confirmAction}</b> on milestone
                  <span className="font-mono ml-1">{mid.slice(0, 12)}…</span>
                  {state?.booking_id && (
                    <> — booking <span className="font-mono">{state.booking_id.slice(0, 12)}…</span></>
                  )}
                </div>
                <div className="text-[11px] text-rose-700">
                  Two-person rule: a second TreatX admin must approve this
                  request before the milestone transitions. You cannot approve
                  your own request.
                </div>
                <div>
                  <Label className="text-[10px] uppercase text-slate-500">Reason (mandatory)</Label>
                  <textarea
                    value={reason}
                    onChange={(e) => setReason(e.target.value)}
                    rows={3}
                    minLength={8}
                    maxLength={1000}
                    placeholder="Why is this override required? (min 8 chars, recorded in audit trail)"
                    className="w-full mt-1 rounded border border-slate-300 px-2 py-1 text-sm font-normal"
                    data-testid="recovery-reason-input"
                  />
                  <div className="text-[10px] text-slate-400">
                    {(reason || '').trim().length}/8 minimum
                  </div>
                </div>
                <div className="flex gap-2">
                  <Button size="sm" variant="ghost"
                          onClick={() => { setConfirmAction(null); setReason(''); }}
                          data-testid="recovery-cancel-btn">Cancel</Button>
                  <Button size="sm" className="bg-rose-700 hover:bg-rose-800 text-white"
                          disabled={busy || (reason || '').trim().length < 8}
                          onClick={submitRequest}
                          data-testid="recovery-submit-btn">Submit request</Button>
                </div>
              </div>
            )}
          </>
        )}

        {tab === 'queue' && (
          <div className="space-y-2" data-testid="recovery-queue">
            <div className="flex items-center justify-between">
              <p className="text-[11px] text-slate-500">
                Only override requests from another admin are actionable by you.
              </p>
              <Button variant="ghost" size="sm" onClick={loadQueue}
                      disabled={queueLoading} className="h-7 px-2 text-xs"
                      data-testid="recovery-queue-refresh">
                <RefreshCw className={`h-3 w-3 mr-1 ${queueLoading ? 'animate-spin' : ''}`} />
                Refresh
              </Button>
            </div>

            {queue.length === 0 && (
              <p className="text-xs text-slate-400 text-center py-4"
                 data-testid="recovery-queue-empty">
                No recovery requests yet.
              </p>
            )}

            <div className="space-y-2">
              {queue.map((q) => {
                const isMine = q.requested_by === currentActorId;
                const isPending = q.status === 'pending';
                return (
                  <div key={q.id}
                       className={`rounded border p-2.5 text-xs ${
                         q.status === 'approved' ? 'border-emerald-200 bg-emerald-50' :
                         q.status === 'rejected' ? 'border-slate-200 bg-slate-50' :
                         q.status === 'failed'   ? 'border-amber-200 bg-amber-50' :
                                                    'border-rose-200 bg-rose-50/40'
                       }`}
                       data-testid={`recovery-queue-item-${q.id}`}>
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2 flex-wrap">
                          <Badge variant="outline" className="text-[10px]">
                            {q.action}
                          </Badge>
                          <Badge className={`text-[10px] ${
                            q.status === 'approved' ? 'bg-emerald-600' :
                            q.status === 'rejected' ? 'bg-slate-500' :
                            q.status === 'failed'   ? 'bg-amber-600' :
                                                       'bg-rose-600'
                          }`}>{q.status}</Badge>
                          <span className="font-mono text-[10.5px] text-slate-500">
                            {(q.milestone_id || '').slice(0, 12)}…
                          </span>
                        </div>
                        <div className="text-slate-700 mt-1 whitespace-pre-wrap">
                          <span className="text-[10px] uppercase text-slate-500 mr-1">Reason</span>
                          {q.reason}
                        </div>
                        <div className="text-[10.5px] text-slate-500 mt-1">
                          Requested by <span className="font-mono">{q.requested_by || '—'}</span>
                          {q.requested_at && <> · {formatDateTimeLocale(q.requested_at)}</>}
                        </div>
                        {q.approved_by && (
                          <div className="text-[10.5px] text-emerald-700 mt-0.5">
                            Approved by <span className="font-mono">{q.approved_by}</span>
                            {q.milestone_status_after && <> · milestone → {q.milestone_status_after}</>}
                          </div>
                        )}
                        {q.rejected_by && (
                          <div className="text-[10.5px] text-slate-600 mt-0.5">
                            Rejected by <span className="font-mono">{q.rejected_by}</span>
                            {q.reject_reason && <> · {q.reject_reason}</>}
                          </div>
                        )}
                        {q.execution_error && (
                          <div className="text-[10.5px] text-amber-800 mt-0.5">
                            Execution error: {q.execution_error}
                          </div>
                        )}
                      </div>
                      {isPending && !isMine && (
                        <div className="flex flex-col gap-1 shrink-0">
                          <Button size="sm" className="h-7 text-[11px] bg-emerald-600 hover:bg-emerald-700 text-white"
                                  disabled={busy}
                                  onClick={() => approveReq(q.id)}
                                  data-testid={`recovery-approve-${q.id}`}>Approve</Button>
                          <Button size="sm" variant="outline" className="h-7 text-[11px]"
                                  disabled={busy}
                                  onClick={() => { setRejectFor(q.id); setRejectReason(''); }}
                                  data-testid={`recovery-reject-${q.id}`}>Reject</Button>
                        </div>
                      )}
                      {isPending && isMine && (
                        <span className="text-[10px] text-slate-500 shrink-0 self-start"
                              data-testid={`recovery-mine-${q.id}`}>
                          Your request · needs another admin
                        </span>
                      )}
                    </div>
                    {rejectFor === q.id && (
                      <div className="mt-2 border-t border-slate-200 pt-2"
                           data-testid={`recovery-reject-panel-${q.id}`}>
                        <Label className="text-[10px] uppercase text-slate-500">Reject reason</Label>
                        <textarea rows={2} value={rejectReason}
                          onChange={(e) => setRejectReason(e.target.value)}
                          className="w-full mt-1 rounded border border-slate-300 px-2 py-1 text-xs"
                          data-testid={`recovery-reject-reason-${q.id}`} />
                        <div className="flex gap-1 mt-1">
                          <Button size="sm" variant="ghost"
                                  className="h-6 text-[11px]"
                                  onClick={() => setRejectFor(null)}
                                  data-testid={`recovery-reject-cancel-${q.id}`}>Cancel</Button>
                          <Button size="sm"
                                  className="h-6 text-[11px] bg-slate-700 hover:bg-slate-800 text-white"
                                  disabled={busy}
                                  onClick={() => rejectReq(q.id)}
                                  data-testid={`recovery-reject-confirm-${q.id}`}>
                            Confirm reject
                          </Button>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

export default function AdminAutoReleasePage() {
  const { admin } = useTreatXAdminAuth();
  const navigate = useNavigate();
  const [health, setHealth] = useState(null);
  const [stats, setStats] = useState(null);
  const [healthLoading, setHealthLoading] = useState(true);
  const [statsLoading, setStatsLoading] = useState(true);

  const fetchHealth = useCallback(async () => {
    setHealthLoading(true);
    try {
      const res = await api.get(`/admin/auto-release/health`);
      setHealth(res.data);
    } catch {}
    finally { setHealthLoading(false); }
  }, []);

  const fetchStats = useCallback(async () => {
    setStatsLoading(true);
    try {
      const res = await api.get(`/admin/auto-release/stats`);
      setStats(res.data);
    } catch {}
    finally { setStatsLoading(false); }
  }, []);

  useEffect(() => { fetchHealth(); fetchStats(); }, [fetchHealth, fetchStats]);

  if (!admin) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F8FAFC]">
        <Card className="w-96">
          <CardContent className="p-6 text-center">
            <Shield className="h-10 w-10 text-slate-300 mx-auto mb-3" />
            <p className="text-sm text-slate-500 mb-3">Admin login required</p>
            <Button onClick={() => navigate('/login')} data-testid="login-redirect">Sign In</Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F8FAFC]" data-testid="admin-auto-release-page">
      <header className="bg-[#122033] text-white border-b border-white/10">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="sm" className="text-white/70 hover:text-white hover:bg-white/10"
              onClick={() => navigate('/treatx-admin/dashboard')} data-testid="back-to-admin">
              <ArrowLeft className="h-4 w-4 mr-1" /> Admin
            </Button>
            <div className="h-5 w-px bg-white/20" />
            <h1 className="text-lg font-semibold tracking-tight font-[Manrope] flex items-center gap-2">
              <Zap className="h-4.5 w-4.5" /> Auto-Release Scheduler
            </h1>
          </div>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-6 space-y-5">
        <HealthPanel health={health} loading={healthLoading} onRefresh={fetchHealth} />
        <StatsPanel stats={stats} loading={statsLoading} onRefresh={fetchStats} />
        <ClinicOverridePanel />
        <MilestoneRecoveryPanel currentActorId={admin?.user_id || admin?.email || admin?.id} />
      </main>
    </div>
  );
}
